import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, Card, Form, Pagination, Spinner, Table } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import ApiService from 'helpers/apiService';
import type { ScoringHistoryItem } from '../../types/scoring';
import { primaryName, secondaryContact } from '../aml/formatUser';

/**
 * v47: «История скоринга» — отдельная страница в группе Финмониторинг.
 * v48: SH-1 navigate с caseId / SH-2 AbortController / UX-2 Crystal placeholder /
 *      SH-3 primaryName/secondaryContact util / SH-4 типы вынесены в src/types/ /
 *      SH-5 aria-label / controlId.
 */

const OUTCOME_BADGE: Record<string, { bg: string; label: string }> = {
  approve:          { bg: 'success',  label: 'Approve' },
  decline:          { bg: 'danger',   label: 'Decline' },
  block_and_review: { bg: 'warning',  label: 'Block & Review' },
  review:           { bg: 'info',     label: 'Review' },
};

const OPERATION_LABEL: Record<string, string> = {
  DEPOSIT_OPERATION:  'Депозит',
  WITHDRAW_OPERATION: 'Вывод',
  TRANSFER_OPERATION: 'Перевод',
  TRADE_OPERATION:    'Обмен',
};

function shortHash(h?: string, head = 8, tail = 6): string {
  if (!h) return '';
  if (h.length <= head + tail + 1) return h;
  return `${h.slice(0, head)}…${h.slice(-tail)}`;
}

function formatDateTime(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ru-RU');
}

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

const ScoringHistoryPage: React.FC = () => {
  const navigate = useNavigate();

  const [items, setItems] = useState<ScoringHistoryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(50);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // фильтры
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [outcome, setOutcome] = useState<string>('');

  // v48 SH-2: AbortController отменяет предыдущий запрос при быстрой смене
  // фильтров — без этого порядок ответов не гарантирован и таблица показывала
  // устаревшие данные (race condition).
  const inflightRef = useRef<AbortController | null>(null);

  const fetchData = useCallback(async () => {
    inflightRef.current?.abort();
    const ctrl = new AbortController();
    inflightRef.current = ctrl;

    setLoading(true);
    setError(null);
    try {
      const r = await ApiService.scoringHistoryGet({
        page,
        size,
        userQ: appliedSearch,
        outcome,
        signal: ctrl.signal,
      });
      // Защита: пока ждали ответ, фильтры могли поменяться → стартовал новый
      // запрос, этот стал устаревшим. AbortController должен был отменить его,
      // но если ответ всё-таки пришёл — игнорируем по signal.aborted.
      if (ctrl.signal.aborted) return;
      setItems(r.items);
      setTotal(r.total);
    } catch (e: any) {
      // axios v1 кидает CanceledError при abort — это не ошибка, просто отменили.
      if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED' || ctrl.signal.aborted) return;
      setError(e?.message || 'Не удалось загрузить историю скоринга');
      setItems([]);
      setTotal(0);
    } finally {
      if (!ctrl.signal.aborted) setLoading(false);
    }
  }, [page, size, appliedSearch, outcome]);

  useEffect(() => {
    fetchData();
    return () => { inflightRef.current?.abort(); };
  }, [fetchData]);

  const applySearch = () => {
    setAppliedSearch(searchInput.trim());
    setPage(1);
  };

  const onOutcomeChange = (v: string) => {
    setOutcome(v);
    setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(total / size));
  const counts = {
    total,
    approved: items.filter(i => i.is_ok).length,
    declined: items.filter(i => i.marble_outcome === 'decline').length,
    blocked:  items.filter(i => i.marble_outcome === 'block_and_review').length,
    review:   items.filter(i => i.marble_outcome === 'review').length,
  };

  return (
    <>
      <Card className="mb-3">
        <Card.Body>
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div>
              <h3 className="mb-0">История скоринга</h3>
              <p className="mt-1 mb-0 text-muted small">
                Все scoring-операции из Marble и Crystal провайдеров, включая успешные.
                AML-кейсы (фрод) показываются с пометкой и ссылкой на карточку.
              </p>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              <Badge bg="dark" style={{ fontSize: '0.78rem', padding: '5px 10px' }}>
                Всего на странице: {items.length} / {total}
              </Badge>
              <Badge bg="success" style={{ fontSize: '0.78rem', padding: '5px 10px' }}>
                OK: {counts.approved}
              </Badge>
              <Badge bg="danger" style={{ fontSize: '0.78rem', padding: '5px 10px' }}>
                Decline: {counts.declined}
              </Badge>
              <Badge bg="warning" text="dark" style={{ fontSize: '0.78rem', padding: '5px 10px' }}>
                Block&Review: {counts.blocked}
              </Badge>
              <Badge bg="info" style={{ fontSize: '0.78rem', padding: '5px 10px' }}>
                Review: {counts.review}
              </Badge>
            </div>
          </div>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          <div className="d-flex flex-wrap align-items-end gap-2 mb-3">
            <Form.Group controlId="scoring-history-search" style={{ minWidth: 280, flex: 1 }}>
              <Form.Label className="small mb-1">
                Поиск (email / phone / UUID / tx_hash / имя)
              </Form.Label>
              <div className="d-flex gap-2">
                <Form.Control
                  size="sm"
                  type="search"
                  placeholder="например: 79805459950"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') applySearch(); }}
                  aria-label="Поиск по пользователю или транзакции"
                />
                <Button variant="outline-primary" size="sm" onClick={applySearch} aria-label="Применить фильтр поиска">
                  Найти
                </Button>
                {appliedSearch && (
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => { setSearchInput(''); setAppliedSearch(''); setPage(1); }}
                    aria-label="Сбросить поиск"
                  >
                    Сброс
                  </Button>
                )}
              </div>
            </Form.Group>

            <Form.Group controlId="scoring-history-outcome" style={{ width: 200 }}>
              <Form.Label className="small mb-1">Outcome</Form.Label>
              <Form.Select size="sm" value={outcome} onChange={(e) => onOutcomeChange(e.target.value)} aria-label="Фильтр по результату скоринга">
                <option value="">Все</option>
                <option value="approve">Approve (OK)</option>
                <option value="decline">Decline</option>
                <option value="block_and_review">Block & Review</option>
                <option value="review">Review</option>
                <option value="failed">Failed (decline + block_and_review)</option>
              </Form.Select>
            </Form.Group>

            <Form.Group controlId="scoring-history-size" style={{ width: 130 }}>
              <Form.Label className="small mb-1">На странице</Form.Label>
              <Form.Select
                size="sm"
                value={size}
                onChange={(e) => { setSize(Number(e.target.value)); setPage(1); }}
                aria-label="Количество строк на странице"
              >
                {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
              </Form.Select>
            </Form.Group>

            <Button variant="outline-secondary" size="sm" onClick={fetchData} disabled={loading} aria-label="Обновить таблицу">
              {loading ? <Spinner size="sm" animation="border" /> : 'Обновить'}
            </Button>
          </div>

          {/* UX-2: подсказка когда Crystal не выполнял проверок — оператор увидит,
              что пустые tx_hash/address для большинства строк — не баг, а норма
              для fiat-операций или после Marble decline (short-circuit). */}
          {useMemo(() => {
            const total = items.length;
            if (total === 0) return null;
            const withTxHash = items.filter(i => i.tx_hash).length;
            if (withTxHash === 0) {
              return (
                <div className="alert alert-info py-2 small mb-2">
                  <strong>Crystal не выполнял KYT-проверок</strong> для записей на этой странице.
                  Возможные причины: лицензия Crystal API истекла, scoring-сервис пропустил Crystal
                  (short-circuit после Marble decline), либо это fiat-операции (Crystal не применим).
                  Колонки Tx Hash / From / To будут пустыми.
                </div>
              );
            }
            return null;
          }, [items])}

          {error && (
            <div className="alert alert-danger py-2 small mb-2">{error}</div>
          )}

          {loading && items.length === 0 ? (
            <div className="text-center py-5">
              <Spinner animation="border" size="sm" className="me-2" />
              <span className="text-muted small">Загрузка…</span>
            </div>
          ) : (
            <Table hover responsive size="sm" className="align-middle mb-0" style={{ fontSize: '0.82rem' }}>
              <thead className="table-light">
                <tr>
                  <th style={{ minWidth: 140 }}>Дата</th>
                  <th style={{ minWidth: 200 }}>Пользователь</th>
                  <th>Операция</th>
                  <th className="text-end">Сумма</th>
                  <th>Outcome</th>
                  <th className="text-center">Score</th>
                  <th>Tx Hash</th>
                  <th>From → To</th>
                  <th>Blockchain</th>
                  <th>AML</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => {
                  const o = it.marble_outcome ?? '';
                  const badge = OUTCOME_BADGE[o];
                  return (
                    <tr key={it.scoring_request_id}>
                      <td className="text-muted">{formatDateTime(it.created_at)}</td>
                      <td>
                        <div className="fw-semibold">
                          {primaryName({ userName: it.user_name, userEmail: it.user_email, userPhone: it.user_phone })}
                        </div>
                        <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                          {secondaryContact({ userName: it.user_name, userEmail: it.user_email, userPhone: it.user_phone })}
                        </div>
                        <code className="text-muted" style={{ fontSize: '0.7rem' }}>
                          {shortHash(it.user_id, 8, 4)}
                        </code>
                      </td>
                      <td>{OPERATION_LABEL[it.operation_type ?? ''] || it.operation_type || '—'}</td>
                      <td className="text-end">
                        {it.amount ? (
                          <>
                            <span className="fw-semibold">{it.amount}</span>{' '}
                            <span className="text-muted">{it.currency || it.asset || ''}</span>
                          </>
                        ) : '—'}
                      </td>
                      <td>
                        {badge ? (
                          <Badge bg={badge.bg} text={badge.bg === 'warning' ? 'dark' : undefined}>
                            {badge.label}
                          </Badge>
                        ) : it.is_ok ? (
                          <Badge bg="success">OK</Badge>
                        ) : (
                          <Badge bg="secondary">—</Badge>
                        )}
                      </td>
                      <td className="text-center">
                        {it.marble_score != null && it.marble_score > 0
                          ? it.marble_score
                          : it.risk_score || '—'}
                      </td>
                      <td>
                        {it.tx_hash ? (
                          <code style={{ fontSize: '0.72rem' }} title={it.tx_hash}>
                            {shortHash(it.tx_hash, 10, 8)}
                          </code>
                        ) : '—'}
                      </td>
                      <td style={{ fontSize: '0.72rem' }}>
                        {(it.from_address || it.to_address) ? (
                          <>
                            <div title={it.from_address}>{shortHash(it.from_address, 8, 6) || '—'}</div>
                            <div className="text-muted">↓</div>
                            <div title={it.to_address}>{shortHash(it.to_address, 8, 6) || '—'}</div>
                          </>
                        ) : '—'}
                      </td>
                      <td className="small text-muted">{it.blockchain || '—'}</td>
                      <td>
                        {it.aml_case_id ? (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            style={{ fontSize: '0.7rem', padding: '2px 6px' }}
                            // v48 SH-1: передаём caseId/alertId через query
                            // → AmlTab.tsx читает useSearchParams и автоматически
                            // открывает соответствующий кейс. Без этого оператор
                            // искал нужный номер вручную в таблице.
                            onClick={() => navigate(`/finmonitoring?caseId=${encodeURIComponent(it.aml_case_id!)}&alertId=${encodeURIComponent(it.aml_alert_id || '')}`)}
                            title={`AML кейс ${it.aml_case_id}`}
                            aria-label={`Открыть AML кейс ${it.aml_case_id}`}
                          >
                            {it.aml_case_id}
                          </Button>
                        ) : (
                          <span className="text-muted small">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {items.length === 0 && !loading && (
                  <tr>
                    <td colSpan={10} className="text-muted text-center py-4">
                      Нет данных
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}

          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div className="text-muted small">
                Страница {page} из {totalPages} · Всего: {total}
              </div>
              <Pagination size="sm" className="mb-0">
                <Pagination.First disabled={page === 1} onClick={() => setPage(1)} />
                <Pagination.Prev disabled={page === 1} onClick={() => setPage(Math.max(1, page - 1))} />
                <Pagination.Item active>{page}</Pagination.Item>
                <Pagination.Next disabled={page >= totalPages} onClick={() => setPage(Math.min(totalPages, page + 1))} />
                <Pagination.Last disabled={page >= totalPages} onClick={() => setPage(totalPages)} />
              </Pagination>
            </div>
          )}
        </Card.Body>
      </Card>
    </>
  );
};

export default ScoringHistoryPage;
